import {
  userErrorMessage,
  assignedClassGamesResponseSchema,
  classMemberResponseSchema,
  classMembersResponseSchema,
  classesResponseSchema,
  classResponseSchema,
  createClassGameResponseSchema,
  createManagedAccountResponseSchema,
  organizationMemberResponseSchema,
  organizationMembersResponseSchema,
  teacherGameDetailResponseSchema,
  teacherGameMutationResponseSchema,
  teacherGamesResponseSchema,
  deleteTeacherGameRecordResponseSchema,
  type AssignedClassGame,
  type ClassMember,
  type ClassSummary,
  type CreatorColorPreference,
  type GameType,
  type ManagedOrganizationMember,
  type StoneColor,
  type TeacherGameDetailResponse,
  type TeacherGameSummary,
  type TimedGameSnapshot,
} from '@wx-weiqi/contracts';

import { nextRequestId } from '../services/http';
import type { IdentityStore } from './identity-store';

export interface TeachingState {
  readonly classes: readonly ClassSummary[];
  readonly students: readonly ManagedOrganizationMember[];
  readonly membersByClass: Readonly<Record<string, readonly ClassMember[]>>;
  readonly teacherGames: readonly TeacherGameSummary[];
  readonly teacherGameFilters: TeacherGameFilters;
  readonly assignedGames: readonly AssignedClassGame[];
  readonly gameDetail: TeacherGameDetailResponse['data'] | null;
  readonly pending: string | null;
  readonly error: string | null;
}

type Listener = () => void;
const MAX_TEACHING_DASHBOARD_PAGES = 100;

export interface ClassGameCreationInput {
  readonly classId?: string;
  readonly playerOneMembershipId: string;
  readonly playerTwoMembershipId: string;
  readonly boardSize: 9 | 13 | 19;
  readonly mainTimeMs: number;
  readonly byoyomiPeriodTimeMs: 10_000 | 15_000 | 30_000 | 60_000 | null;
  readonly byoyomiPeriods: 1 | 2 | 3 | 5 | 10;
  readonly gameType: GameType;
  readonly playerOneColor: CreatorColorPreference;
}

export interface GuidedGameCreationInput {
  readonly classId?: string;
  readonly studentMembershipId: string;
  readonly boardSize: 9 | 13 | 19;
  readonly mainTimeMs: number;
  readonly byoyomiPeriodTimeMs: 10_000 | 15_000 | 30_000 | 60_000 | null;
  readonly byoyomiPeriods: 1 | 2 | 3 | 5 | 10;
  readonly gameType: GameType;
}

export interface TeacherGameFilters {
  readonly classId?: string;
  readonly studentMembershipId?: string;
  readonly status?: readonly TimedGameSnapshot['status'][];
}

export function teacherGamesRequestPath(filters: TeacherGameFilters): string {
  const query = new URLSearchParams({ limit: '50' });
  for (const status of filters.status ?? []) query.append('status', status);
  if (filters.classId !== undefined && filters.classId.length > 0) {
    query.set('classId', filters.classId);
  }
  if (filters.studentMembershipId !== undefined && filters.studentMembershipId.length > 0) {
    query.set('studentMembershipId', filters.studentMembershipId);
  }
  return `/teacher/games?${query.toString()}`;
}

export class TeachingStore {
  async deleteGameRecord(game: TeacherGameSummary): Promise<void> {
    return this.removeGame(game, true);
  }

  async deleteGame(game: TeacherGameSummary): Promise<void> {
    return this.removeGame(game, false);
  }

  private async removeGame(game: TeacherGameSummary, recordOnly: boolean): Promise<void> {
    this.update({ ...this.stateValue, pending: 'delete-game-record', error: null });
    try {
      await this.identity.authorized(
        `/teacher/games/${encodeURIComponent(game.gameId)}${recordOnly ? '/record' : ''}`,
        deleteTeacherGameRecordResponseSchema,
        {
          method: 'DELETE',
          requestId: nextRequestId('web-delete-kifu'),
          body: { stateVersion: game.stateVersion },
        },
      );
      this.update({
        ...this.stateValue,
        teacherGames: this.stateValue.teacherGames.filter((item) => item.gameId !== game.gameId),
        assignedGames: this.stateValue.assignedGames.filter((item) => item.gameId !== game.gameId),
        gameDetail:
          this.stateValue.gameDetail?.snapshot.gameId === game.gameId
            ? null
            : this.stateValue.gameDetail,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.update({
        ...this.stateValue,
        pending: null,
        error: userErrorMessage(error, '删除失败，请重试。'),
      });
      throw error;
    }
  }
  private stateValue: TeachingState = {
    classes: [],
    students: [],
    membersByClass: {},
    teacherGames: [],
    teacherGameFilters: {},
    assignedGames: [],
    gameDetail: null,
    pending: null,
    error: null,
  };
  private readonly listeners = new Set<Listener>();

  constructor(
    private readonly identity: IdentityStore,
    private readonly currentOrganizationId: () => string | null,
    private readonly currentMembershipId: () => string | null,
  ) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): TeachingState => this.stateValue;

  clear(): void {
    this.update({
      classes: [],
      students: [],
      membersByClass: {},
      teacherGames: [],
      teacherGameFilters: {},
      assignedGames: [],
      gameDetail: null,
      pending: null,
      error: null,
    });
  }

  async loadTeacherDashboard(): Promise<void> {
    const organizationId = this.requireOrganizationId();
    this.update({
      ...this.stateValue,
      classes: [],
      students: [],
      membersByClass: {},
      pending: 'load-teacher',
      error: null,
    });
    try {
      const [classes, students] = await Promise.all([
        this.loadAllClasses(),
        this.loadAllStudents(organizationId),
      ]);
      const memberPages = await Promise.all(
        classes.map(
          async (managedClass) =>
            [managedClass.classId, await this.loadAllClassMembers(managedClass.classId)] as const,
        ),
      );
      const membersByClass = Object.fromEntries(memberPages);
      this.update({
        ...this.stateValue,
        classes,
        students,
        membersByClass,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error, '教师数据加载失败，请重试。');
      throw error;
    }
  }

  async createClass(name: string, termLabel: string): Promise<void> {
    const teacherMembershipId = this.requireMembershipId();
    this.pending('create-class');
    try {
      await this.identity.authorized('/classes', classResponseSchema, {
        method: 'POST',
        requestId: nextRequestId('web-class-create'),
        body: {
          name: name.trim(),
          termLabel: termLabel.trim().length === 0 ? null : termLabel.trim(),
          teacherMembershipId,
        },
      });
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '班级创建失败。');
      throw error;
    }
  }

  async archiveClass(managedClass: ClassSummary): Promise<void> {
    this.pending(`archive-class-${managedClass.classId}`);
    try {
      await this.identity.authorized(
        `/classes/${encodeURIComponent(managedClass.classId)}`,
        classResponseSchema,
        {
          method: 'PATCH',
          requestId: nextRequestId('web-class-archive'),
          body: { scopeVersion: managedClass.scopeVersion, status: 'ARCHIVED' },
        },
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '班级删除失败。');
      throw error;
    }
  }

  async createStudent(input: {
    readonly username: string;
    readonly password: string;
    readonly displayName: string;
  }): Promise<void> {
    const organizationId = this.requireOrganizationId();
    this.pending('create-student');
    try {
      await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/accounts`,
        createManagedAccountResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-student-create'),
          body: {
            username: input.username.trim(),
            password: input.password,
            displayName: input.displayName.trim(),
            role: 'STUDENT',
          },
        },
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '学生账号创建失败。');
      throw error;
    }
  }

  async setStudentActive(student: ManagedOrganizationMember, active: boolean): Promise<void> {
    const organizationId = this.requireOrganizationId();
    this.pending(`student-${student.membershipId}`);
    try {
      await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(student.membershipId)}`,
        organizationMemberResponseSchema,
        {
          method: 'PATCH',
          requestId: nextRequestId('web-student-status'),
          body: { version: student.version, status: active ? 'ACTIVE' : 'INACTIVE' },
        },
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, active ? '学生账号恢复失败。' : '学生账号删除失败。');
      throw error;
    }
  }

  async updateStudentProfile(
    student: ManagedOrganizationMember,
    displayName: string,
  ): Promise<void> {
    const organizationId = this.requireOrganizationId();
    this.pending(`student-profile-${student.membershipId}`);
    try {
      await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/accounts/${encodeURIComponent(student.membershipId)}/profile`,
        organizationMemberResponseSchema,
        {
          method: 'PATCH',
          requestId: nextRequestId('web-student-profile'),
          body: {
            profileVersion: student.user.profileVersion,
            displayName: displayName.trim(),
          },
        },
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '学生信息修改失败。');
      throw error;
    }
  }

  async resetStudentPassword(student: ManagedOrganizationMember, password: string): Promise<void> {
    const organizationId = this.requireOrganizationId();
    this.pending(`student-password-${student.membershipId}`);
    try {
      await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/accounts/${encodeURIComponent(student.membershipId)}/password-reset`,
        organizationMemberResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-student-password'),
          body: {
            profileVersion: student.user.profileVersion,
            password,
          },
        },
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '学生密码修改失败。');
      throw error;
    }
  }

  async setStudentClasses(
    student: ManagedOrganizationMember,
    targetClassIds: readonly string[],
  ): Promise<void> {
    const selected = new Set(targetClassIds);
    const current = new Set(
      this.stateValue.classes
        .filter((managedClass) =>
          (this.stateValue.membersByClass[managedClass.classId] ?? []).some(
            (member) => member.membership.membershipId === student.membershipId,
          ),
        )
        .map((managedClass) => managedClass.classId),
    );
    const changes = this.stateValue.classes.filter(
      (managedClass) => selected.has(managedClass.classId) !== current.has(managedClass.classId),
    );
    this.pending(`student-classes-${student.membershipId}`);
    try {
      await Promise.all(
        changes.map((managedClass) =>
          selected.has(managedClass.classId)
            ? this.identity.authorized(
                `/classes/${encodeURIComponent(managedClass.classId)}/members`,
                classMemberResponseSchema,
                {
                  method: 'POST',
                  requestId: nextRequestId('web-student-class-add'),
                  body: {
                    scopeVersion: managedClass.scopeVersion,
                    membershipId: student.membershipId,
                  },
                },
              )
            : this.identity.authorized(
                `/classes/${encodeURIComponent(managedClass.classId)}/members/${encodeURIComponent(student.membershipId)}`,
                classMemberResponseSchema,
                {
                  method: 'DELETE',
                  requestId: nextRequestId('web-student-class-remove'),
                  body: { scopeVersion: managedClass.scopeVersion },
                },
              ),
        ),
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '学生班级调整失败。');
      throw error;
    }
  }

  async importStudent(managedClass: ClassSummary, membershipId: string): Promise<void> {
    this.pending(`class-member-${managedClass.classId}`);
    try {
      await this.identity.authorized(
        `/classes/${encodeURIComponent(managedClass.classId)}/members`,
        classMemberResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-class-member-add'),
          body: { scopeVersion: managedClass.scopeVersion, membershipId },
        },
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '学生导入班级失败。');
      throw error;
    }
  }

  async removeStudentFromClass(managedClass: ClassSummary, membershipId: string): Promise<void> {
    this.pending(`class-member-${managedClass.classId}`);
    try {
      await this.identity.authorized(
        `/classes/${encodeURIComponent(managedClass.classId)}/members/${encodeURIComponent(membershipId)}`,
        classMemberResponseSchema,
        {
          method: 'DELETE',
          requestId: nextRequestId('web-class-member-remove'),
          body: { scopeVersion: managedClass.scopeVersion },
        },
      );
      await this.loadTeacherDashboard();
    } catch (error: unknown) {
      this.fail(error, '移出班级失败。');
      throw error;
    }
  }

  async createClassGame(input: ClassGameCreationInput): Promise<TimedGameSnapshot> {
    this.pending('create-class-game');
    try {
      const response = await this.identity.authorized(
        input.classId
          ? `/classes/${encodeURIComponent(input.classId)}/games`
          : '/teacher/student-games',
        createClassGameResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-class-game-create'),
          body: {
            playerOneMembershipId: input.playerOneMembershipId,
            playerTwoMembershipId: input.playerTwoMembershipId,
            boardSize: input.boardSize,
            timing: {
              ruleCode: 'ABSOLUTE_V1',
              mainTimeMs: input.mainTimeMs,
              ...(input.byoyomiPeriodTimeMs === null
                ? {}
                : {
                    byoyomi: {
                      periodTimeMs: input.byoyomiPeriodTimeMs,
                      periods: input.byoyomiPeriods,
                    },
                  }),
            },
            gameType: input.gameType,
            playerOneColor: input.playerOneColor,
          },
        },
      );
      this.update({ ...this.stateValue, pending: null, error: null });
      return response.data;
    } catch (error: unknown) {
      this.fail(error, '班级学生对局创建失败。');
      throw error;
    }
  }

  async createGuidedGame(input: GuidedGameCreationInput): Promise<TimedGameSnapshot> {
    this.pending('create-guided-game');
    try {
      const response = await this.identity.authorized(
        input.classId
          ? `/classes/${encodeURIComponent(input.classId)}/guided-games`
          : '/teacher/guided-games',
        createClassGameResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-guided-game-create'),
          body: {
            studentMembershipId: input.studentMembershipId,
            boardSize: input.boardSize,
            timing: {
              ruleCode: 'ABSOLUTE_V1',
              mainTimeMs: input.mainTimeMs,
              ...(input.byoyomiPeriodTimeMs === null
                ? {}
                : {
                    byoyomi: {
                      periodTimeMs: input.byoyomiPeriodTimeMs,
                      periods: input.byoyomiPeriods,
                    },
                  }),
            },
            gameType: input.gameType,
          },
        },
      );
      this.update({ ...this.stateValue, pending: null, error: null });
      return response.data;
    } catch (error: unknown) {
      this.fail(error, '指导对弈创建失败。');
      throw error;
    }
  }

  async loadTeacherGames(filters: TeacherGameFilters = {}): Promise<void> {
    this.update({
      ...this.stateValue,
      gameDetail: null,
      teacherGames: [],
      teacherGameFilters: filters,
      pending: 'load-teacher-games',
      error: null,
    });
    try {
      const response = await this.identity.authorized(
        teacherGamesRequestPath(filters),
        teacherGamesResponseSchema,
      );
      this.update({
        ...this.stateValue,
        teacherGames: response.data.games,
        teacherGameFilters: filters,
        gameDetail: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error, '学生对局列表加载失败。');
      throw error;
    }
  }

  async loadTeacherGameDetail(gameId: string): Promise<void> {
    this.pending(`teacher-game-${gameId}`);
    try {
      const response = await this.identity.authorized(
        `/teacher/games/${encodeURIComponent(gameId)}`,
        teacherGameDetailResponseSchema,
      );
      this.update({ ...this.stateValue, gameDetail: response.data, pending: null, error: null });
    } catch (error: unknown) {
      this.fail(error, '对局详情加载失败。');
      throw error;
    }
  }

  /** Read-only teacher view: use the existing class-scoped authorization, never join a player room. */
  readonly readTeacherGame = async (gameId: string): Promise<TeacherGameDetailResponse['data']> => {
    return (
      await this.identity.authorized(
        `/teacher/games/${encodeURIComponent(gameId)}`,
        teacherGameDetailResponseSchema,
      )
    ).data;
  };

  async correctTeacherGameResult(
    gameId: string,
    stateVersion: number,
    winner: StoneColor,
    internalReason: string,
  ): Promise<TimedGameSnapshot> {
    this.pending(`teacher-result-correction-${gameId}`);
    try {
      const response = await this.identity.authorized(
        `/teacher/games/${encodeURIComponent(gameId)}/result-correction`,
        teacherGameMutationResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-teacher-result-correction'),
          body: {
            stateVersion,
            winner,
            reasonCode: 'OTHER',
            internalReason: internalReason.trim(),
          },
        },
      );
      this.update({ ...this.stateValue, pending: null, error: null });
      return response.data.snapshot;
    } catch (error: unknown) {
      this.fail(error, '学生对局结果修改失败。');
      throw error;
    }
  }

  closeGameDetail(): void {
    this.update({ ...this.stateValue, gameDetail: null });
  }

  async loadAssignedGames(): Promise<void> {
    this.update({
      ...this.stateValue,
      assignedGames: [],
      pending: 'load-assigned-games',
      error: null,
    });
    try {
      const response = await this.identity.authorized(
        '/me/class-games',
        assignedClassGamesResponseSchema,
      );
      this.update({
        ...this.stateValue,
        assignedGames: response.data.games,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error, '班级对弈列表加载失败。');
      throw error;
    }
  }

  clearError(): void {
    this.update({ ...this.stateValue, error: null });
  }

  private async loadAllClasses(): Promise<readonly ClassSummary[]> {
    const classes: ClassSummary[] = [];
    let after: string | null = null;
    for (let page = 0; page < MAX_TEACHING_DASHBOARD_PAGES; page += 1) {
      const query = new URLSearchParams({ status: 'ACTIVE', limit: '100' });
      if (after !== null) query.set('after', after);
      const response = await this.identity.authorized(
        `/classes?${query.toString()}`,
        classesResponseSchema,
      );
      classes.push(...response.data.classes);
      after = response.data.nextAfter;
      if (after === null) return classes;
    }
    throw new Error('班级数据页数超过安全上限。');
  }

  private async loadAllStudents(
    organizationId: string,
  ): Promise<readonly ManagedOrganizationMember[]> {
    const students: ManagedOrganizationMember[] = [];
    let after: string | null = null;
    for (let page = 0; page < MAX_TEACHING_DASHBOARD_PAGES; page += 1) {
      const query = new URLSearchParams({ role: 'STUDENT', limit: '100' });
      if (after !== null) query.set('after', after);
      const response = await this.identity.authorized(
        `/organizations/${encodeURIComponent(organizationId)}/members?${query.toString()}`,
        organizationMembersResponseSchema,
      );
      students.push(...response.data.members);
      after = response.data.nextAfter;
      if (after === null) return students;
    }
    throw new Error('学生数据页数超过安全上限。');
  }

  private async loadAllClassMembers(classId: string): Promise<readonly ClassMember[]> {
    const members: ClassMember[] = [];
    let after: string | null = null;
    for (let page = 0; page < MAX_TEACHING_DASHBOARD_PAGES; page += 1) {
      const query = new URLSearchParams({ status: 'ACTIVE', limit: '100' });
      if (after !== null) query.set('after', after);
      const response = await this.identity.authorized(
        `/classes/${encodeURIComponent(classId)}/members?${query.toString()}`,
        classMembersResponseSchema,
      );
      members.push(...response.data.members);
      after = response.data.nextAfter;
      if (after === null) return members;
    }
    throw new Error('班级学生数据页数超过安全上限。');
  }

  private requireOrganizationId(): string {
    const organizationId = this.currentOrganizationId();
    if (organizationId === null) throw new Error('请先选择棋院身份。');
    return organizationId;
  }

  private requireMembershipId(): string {
    const membershipId = this.currentMembershipId();
    if (membershipId === null) throw new Error('请先选择教师身份。');
    return membershipId;
  }

  private pending(operation: string): void {
    this.update({ ...this.stateValue, pending: operation, error: null });
  }

  private fail(error: unknown, fallback: string): void {
    this.update({
      ...this.stateValue,
      pending: null,
      error: userErrorMessage(error, fallback),
    });
  }

  private update(state: TeachingState): void {
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
  }
}
